import React, { forwardRef, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { Radar } from 'react-chartjs-2';
import {
    Chart,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import CustomRadialScale from './customRadialScale';

Chart.register(PointElement, LineElement, Filler, Tooltip, Legend);

const formatValue = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return 'N/A';
    }
    if (typeof value === 'number') {
        return `${Math.round(value)}`;
    }
    return value;
};

const PDFComponent = forwardRef(({ firm, boxes = [], rankings = [], contactDetails = [] }, ref) => {
    const formattedDate = useMemo(() => {
        const now = new Date();
        return new Intl.DateTimeFormat('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(now);
    }, []);

    const radarDataValues = [
        firm?.size,
        firm?.brandPower,
        firm?.trajectory,
        firm?.arena,
        firm?.handsOnWork,
        firm?.reputation,
        firm?.clients,
        firm?.totalScore,
    ];

    const radarData = {
        labels: [
            'Size',
            'Visibility',
            'Trajectory',
            'Arena',
            'Hands-on Work',
            'Reputation',
            'Clients',
            'Total',
        ],
        datasets: [
            {
                data: radarDataValues,
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                borderWidth: 1,
                pointRadius: 2,
                pointBackgroundColor: '#000000',
            },
        ],
    };

    const radarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            r: {
                type: 'customRadial',
                min: 0,
                max: 100,
                ticks: {
                    display: false,
                    backdropColor: 'transparent',
                },
                grid: {
                    color: '#888888',
                },
                angleLines: {
                    color: '#444444',
                },
                pointLabels: {
                    color: '#000000',
                    font: {
                        family: 'Helvetica, Arial, sans-serif',
                        size: 10,
                    },
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,
            },
        },
    };

    const visibleBoxes = boxes.filter((box) => box && box.title);

    return (
        <Box
            ref={ref}
            sx={{
                width: '816px',
                minHeight: '1056px',
                backgroundColor: '#FFFFFF',
                color: '#000000',
                padding: '48px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontFamily: 'Helvetica, Arial, sans-serif',
            }}
        >
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Informe de Desempeño
                </Typography>
                <Divider sx={{ my: 2, borderColor: '#000000' }} />
                <Grid container spacing={2}>
                    <Grid item xs={7}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {firm?.institutionName || 'Firm Name'}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                            País: {firm?.Country?.name || firm?.country?.name || 'N/A'}
                        </Typography>
                        <Typography variant="body1">
                            Posición local: {formatValue(firm?.localPosition)}
                        </Typography>
                        <Typography variant="body1">
                            Posición global: {formatValue(firm?.globalPosition)}
                        </Typography>
                        <Typography variant="body1">
                            Puntaje total: {formatValue(firm?.totalScore)}
                        </Typography>
                    </Grid>
                    <Grid item xs={5}>
                        <Box sx={{
                            border: '1px solid #000000',
                            padding: 2,
                            height: '100%',
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Información del informe
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Fecha de emisión: {formattedDate}
                            </Typography>
                            <Typography variant="body2">
                                Número de referencia: {firm?.id ? `F-${firm.id}` : 'N/A'}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Resumen de indicadores clave
                </Typography>
                <Grid container spacing={2}>
                    {visibleBoxes.map((box) => (
                        <Grid item xs={6} key={box.key}>
                            <Box
                                sx={{
                                    border: '1px solid #000000',
                                    padding: 2,
                                    minHeight: '96px',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {box.title}
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1 }}>
                                    {formatValue(box.value)}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Desempeño general
                </Typography>
                <Box
                    sx={{
                        border: '1px solid #000000',
                        padding: 2,
                        height: '320px',
                    }}
                >
                    <Radar data={radarData} options={radarOptions} />
                </Box>
            </Box>

            {rankings.length > 0 && (
                <Box sx={{ pageBreakInside: 'avoid' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Áreas rankeadas
                    </Typography>
                    <TableContainer
                        component={Paper}
                        sx={{ border: '1px solid #000000', boxShadow: 'none', backgroundColor: '#FFFFFF' }}
                    >
                        <Table
                            size="small"
                            aria-label="Rankings table"
                            sx={{ '& td, & th': { border: '1px solid #000000' } }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Área</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Chambers &amp; Partners</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Legal 500</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Leaders League</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rankings.map((row) => (
                                    <TableRow key={row.id || `${row.Area?.id}-${row.Area?.nombre}`}>
                                        <TableCell>{row.Area?.nombre || 'N/A'}</TableCell>
                                        <TableCell>{formatValue(row.cp)}</TableCell>
                                        <TableCell>{formatValue(row.L500)}</TableCell>
                                        <TableCell>{formatValue(row.LL)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {contactDetails.length > 0 && (
                <Box sx={{ pageBreakInside: 'avoid' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                        Información de contacto
                    </Typography>
                    <Box sx={{ border: '1px solid #000000', padding: 2 }}>
                        {contactDetails.map((detail, index) => (
                            <Typography key={`${detail.display}-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                                {detail.display}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
});

PDFComponent.displayName = 'PDFComponent';

export default PDFComponent;